<template>
  <loading-mod-collections v-if="panelLoading || loadingCollections" />
  <view-section
    v-else-if="collectionsData?.result_total"
    :title="
      $t('mod_with_collections', { ugc: ugcName(game, true, true).value })
    "
    :padding-x="false"
    :padding-y="false"
  >
    <base-link
      v-for="collection in collections.slice(0, MAX_COLLECTIONS_TO_SHOW)"
      :key="collection.name_id"
      :link-primary="false"
      :to="{
        name: COLLECTION_VIEW_ROUTE,
        params: {
          collection: collection.name_id,
        },
      }"
      class="tw-flex tw-items-center tw-justify-between tw-p-4 tw-space-x-2 game-tile-background-color-hover"
    >
      <div class="tw-flex tw-items-center tw-space-x-2">
        <div
          :style="`background-image: url(${collection.logo.thumb_320x180})`"
          role="image"
          class="tw-w-16 tw-h-9 tw-shrink-0 tw-rounded tw-bg-cover tw-bg-center tw-overflow-hidden"
        />
        <span class="tw-font-medium mod-util-truncate-one-line tw-mr-auto">
          {{ collection.name }}
        </span>
      </div>
      <follow-button
        v-if="Settings.allowSubscribing"
        simple-mode
        :game="game"
        :collection="collection"
        @click.prevent
      />
    </base-link>
    <template v-if="collectionsData?.result_total > MAX_COLLECTIONS_TO_SHOW">
      <div
        :class="Classes.FILE_DIVIDER"
        class="tw-border-t tw-border-[var(--tile-text)] tw-opacity-30"
      />
      <base-button
        no-padding
        class="tw-m-4 tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100"
        text-link-hover
        @click="showCollectionModal"
      >
        {{ $t('see_all_collections', { count: collectionsData.result_total }) }}
      </base-button>
    </template>
  </view-section>

  <teleport to="body">
    <base-modal
      has-close-btn
      :show="isCollectionModalOpen"
      size="md"
      :heading="
        $t('mod_with_collections', { ugc: ugcName(game, true, true).value })
      "
      @close:modal="hideCollectionModal"
    >
      <modio-table
        fetch
        table-id="collections-for-mod"
        :row-schema="schema"
        :items="{ data: collections }"
        :item-total="collectionsData?.result_total"
        :loading="loadingCollections"
        :error="collectionsError"
        :limit="25"
        @fetch:query="fetchQuery"
        @fetch:paginate-next="fetchPaginateNext"
        @fetch:paginate-prev="fetchPaginatePrev"
      />
    </base-modal>

    <collection-subscribe-modal
      v-if="selectedCollection"
      :show="isSubscribeModalOpen"
      :collection="selectedCollection"
      :is-subscribed="isSubscribedToSelected"
      @close:modal="hideSubscribeModal"
    />
  </teleport>
</template>

<script>
import CollectionSubscribeModal from '@components/Modals/CollectionSubscribeModal.vue'
import LoadingModCollections from '@components/Loading/LoadingModCollections.vue'
import collectionSerializer from '@shared/js/serializers/collectionSerializer.js'
import { useResourceValue, useAsync, useRoute, useModal } from '@composables'
import { onBeforeMount, computed, provide, watch, ref } from 'vue'
import FollowButton from '@components/Collection/FollowButton.vue'
import { COLLECTION_VIEW_ROUTE } from '@config/routeNames.js'
import { mergeApiResultData } from '@helpers/requestAll.js'
import ViewSection from '@components/View/ViewSection.vue'
import { schema } from '@config/schemas/modCollections.js'
import ModioTable from '@components/Table/ModioTable.vue'
import { getModCollectionsRequest } from '@services'
import { collectionFollowingStore } from '@stores'
import Classes from '@shared/js/config/css.js'
import { Settings } from '@config/game.js'
import { clone } from '@helpers/utils.js'

const MAX_COLLECTIONS_TO_SHOW = 5

export default {
  components: {
    CollectionSubscribeModal,
    LoadingModCollections,
    FollowButton,
    ViewSection,
    ModioTable,
  },
  props: {
    game: {
      type: Object,
      default: null,
    },
    panelLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const selectedCollection = ref(null)
    const collections = ref([])

    const modNameId = useRoute().getModId().value
    const { ugcName } = useResourceValue()

    const { followedCollections } = collectionFollowingStore()

    const isSubscribedToSelected = computed(() => {
      return followedCollections.value.some(
        (subscription) =>
          subscription?.id === parseInt(selectedCollection.value?.id)
      )
    })

    const {
      isModalOpen: isCollectionModalOpen,
      hideModal: hideCollectionModal,
      showModal: showCollectionModal,
    } = useModal()

    const {
      isModalOpen: isSubscribeModalOpen,
      showModal: showSubscribeModal,
      hideModal: hideSubscribeModal,
    } = useModal()

    const {
      loading: loadingCollections,
      error: collectionsError,
      run: runGetCollections,
      data: collectionsData,
    } = useAsync(() =>
      getModCollectionsRequest(modNameId, {
        _sort: '-followers_total',
      })
    )

    onBeforeMount(fetchQuery)

    async function fetchQuery(query) {
      await runGetCollections(query)

      if (!collectionsError.value) {
        const serialized = collectionSerializer.transform(collectionsData.value)
        collections.value = formatCollections(serialized.data)
      }
    }

    async function fetchPaginateNext(query) {
      await runGetCollections(query)

      if (!collectionsError.value) {
        const serialized = collectionSerializer.transform(collectionsData.value)
        collections.value = formatCollections(
          mergeApiResultData({ data: collections.value }, serialized).data
        )
      }
    }

    async function fetchPaginatePrev(query) {
      await runGetCollections(query)

      if (!collectionsError.value) {
        const serialized = collectionSerializer.transform(collectionsData.value)
        collections.value = formatCollections(
          mergeApiResultData(serialized, { data: collections.value }).data
        )
      }
    }

    function formatCollections(collectionList, sort = true) {
      let result = clone(collectionList)
      if (sort) {
        result = result.sort((a, b) => {
          const aTop = a.stats.ratings_positive / a.stats.ratings_total >= 0.66
          const bTop = b.stats.ratings_positive / b.stats.ratings_total >= 0.66

          if (aTop !== bTop) return bTop - aTop
          return b.stats.followers_total - a.stats.followers_total
        })
      }
      return result.map((c) => ({
        ...c,
        unfollowed: !followedCollections.value.some((s) => s.id === c.id),
      }))
    }

    async function toggleFollow(collection) {
      selectedCollection.value = collection
      showSubscribeModal()
    }
    provide('toggleFollow', toggleFollow)

    watch(followedCollections, () => {
      collections.value = formatCollections(collections.value, false)
    })

    return {
      MAX_COLLECTIONS_TO_SHOW,
      isSubscribedToSelected,
      isCollectionModalOpen,
      COLLECTION_VIEW_ROUTE,
      isSubscribeModalOpen,
      showCollectionModal,
      hideCollectionModal,
      loadingCollections,
      selectedCollection,
      hideSubscribeModal,
      fetchPaginatePrev,
      fetchPaginateNext,
      collectionsError,
      collectionsData,
      collections,
      fetchQuery,
      Settings,
      ugcName,
      Classes,
      schema,
    }
  },
}
</script>
